// Message encoders (protocol §5). `write…(w, …)` appends to a caller-owned Writer (the server reuses
// one per tick); `encode…(…)` returns a fresh Uint8Array ready for `ws.send`.

import {
  HELLO_MAGIC,
  MAX_MASS_SAMPLES,
  MAX_STANDINGS,
  STR_MAX_BYTES,
  WHEEL_ZOOM_WIRE_MAX,
  WHEEL_ZOOM_WIRE_MIN,
} from '../constants.js';
import { clamp } from '../math/clamp.js';
import { writeFrame } from './frame.js';
import {
  ARENA_LIFE_FLAG,
  ARENA_SHAPE_CODE,
  C2S,
  LABEL_CURRENCY,
  OPTIONS_FLAG,
  PLAY_FLAG,
  S2C,
  STANDING_FLAG,
  WELCOME_FLAG,
  WELCOME_SIM,
} from './opcodes.js';
import { DEFAULT_QUANT, fractionToWire, type Quant } from './quant.js';
import type {
  ArenaLifeInfo,
  WelcomeArenaInfo,
  CashOutDoneMessage,
  CashOutStateMessage,
  ClientMessage,
  DeathMessage,
  FrameMessage,
  LifeSummary,
  MatchResultMessage,
  OptionsMessage,
  ServerMessage,
  WelcomeMessage,
} from './types.js';
import { PROTOCOL_VERSION } from './version.js';
import { Writer } from './writer.js';

/** A message without its `op` field (distributes over unions). */
export type MessageBody<T> = T extends unknown ? Omit<T, 'op'> : never;

function wheelZoomWire(z: number): number {
  const q = Number.isFinite(z) ? Math.round(z * 100) : 100;
  return clamp(q, WHEEL_ZOOM_WIRE_MIN, WHEEL_ZOOM_WIRE_MAX);
}

// ---------------------------------------------------------------------------------------------
// Client → server
// ---------------------------------------------------------------------------------------------

/** Appends any client message to `w` (protocol §5.1). */
export function writeClientMessage(w: Writer, msg: ClientMessage): void {
  w.u8(msg.op);
  switch (msg.op) {
    case C2S.HELLO:
      for (const b of HELLO_MAGIC) w.u8(b);
      w.u16(msg.protocolVersion).str(msg.clientBuild, STR_MAX_BYTES.clientBuild);
      // Optional trailer (signed-in players): absent = the pre-auth layout, byte for byte.
      if (msg.authToken !== undefined) w.str(msg.authToken, STR_MAX_BYTES.authToken);
      return;
    case C2S.PLAY: {
      const hasTicket = msg.ticket !== undefined;
      const flags = (msg.flags & ~PLAY_FLAG.HAS_TICKET) | (hasTicket ? PLAY_FLAG.HAS_TICKET : 0);
      w.str(msg.nick, STR_MAX_BYTES.nick).str(msg.skinId, STR_MAX_BYTES.skinId).u8(flags);
      if (hasTicket) w.str(msg.ticket ?? '', STR_MAX_BYTES.ticketId);
      return;
    }
    case C2S.INPUT:
      w.f32(msg.targetX).f32(msg.targetY);
      return;
    case C2S.SPLIT:
    case C2S.EJECT:
    case C2S.SPECIAL:
    case C2S.SPECTATE:
    case C2S.CASH_OUT_START:
    case C2S.CASH_OUT_CANCEL:
      return;
    case C2S.OPTIONS: {
      const flags =
        (msg.wantMinimapPlayers ? OPTIONS_FLAG.WANT_MINIMAP_PLAYERS : 0) |
        (msg.menuOpen ? OPTIONS_FLAG.MENU_OPEN : 0);
      w.u8(flags).u16(wheelZoomWire(msg.wheelZoom));
      return;
    }
    case C2S.PING:
      // "Low 32 bits of performance.now()": fractions are dropped and the value wraps.
      w.u32(Math.floor(msg.clientTimeMs) >>> 0);
      return;
    case C2S.ARENA_INPUT:
      w.u16(msg.heading & 0xffff).u16(msg.seq & 0xffff);
      return;
  }
}

export function encodeClientMessage(msg: ClientMessage): Uint8Array<ArrayBuffer> {
  const w = new Writer(64);
  writeClientMessage(w, msg);
  return w.finish();
}

/**
 * HELLO. `authToken` (a signed-in player's session token, ≤ STR_MAX_BYTES.authToken bytes) is the
 * optional trailer; omit it (or pass `""`) to send the anonymous layout.
 */
export function encodeHello(
  clientBuild: string,
  protocolVersion: number = PROTOCOL_VERSION,
  authToken?: string,
): Uint8Array<ArrayBuffer> {
  return encodeClientMessage(
    authToken === undefined || authToken === ''
      ? { op: C2S.HELLO, protocolVersion, clientBuild }
      : { op: C2S.HELLO, protocolVersion, clientBuild, authToken },
  );
}

/** PLAY. `ticket` (money rooms) is the entry ticket id from the buy-in; omit it in free rooms. */
export function encodePlay(
  nick: string,
  skinId = '',
  flags = 0,
  ticket?: string,
): Uint8Array<ArrayBuffer> {
  return encodeClientMessage(
    ticket === undefined
      ? { op: C2S.PLAY, nick, skinId, flags }
      : { op: C2S.PLAY, nick, skinId, flags, ticket },
  );
}

export function encodeCashOutStart(): Uint8Array<ArrayBuffer> {
  return Uint8Array.of(C2S.CASH_OUT_START);
}

export function encodeCashOutCancel(): Uint8Array<ArrayBuffer> {
  return Uint8Array.of(C2S.CASH_OUT_CANCEL);
}

export function encodeInput(targetX: number, targetY: number): Uint8Array<ArrayBuffer> {
  return encodeClientMessage({ op: C2S.INPUT, targetX, targetY });
}

/** ARENA_INPUT: `heading` in wire steps (`headingToWire`), `seq` u16 (wraps). */
export function encodeArenaInput(heading: number, seq: number): Uint8Array<ArrayBuffer> {
  return encodeClientMessage({ op: C2S.ARENA_INPUT, heading, seq });
}

export function encodeSplit(): Uint8Array<ArrayBuffer> {
  return Uint8Array.of(C2S.SPLIT);
}

export function encodeEject(): Uint8Array<ArrayBuffer> {
  return Uint8Array.of(C2S.EJECT);
}

export function encodeSpecial(): Uint8Array<ArrayBuffer> {
  return Uint8Array.of(C2S.SPECIAL);
}

export function encodeSpectate(): Uint8Array<ArrayBuffer> {
  return Uint8Array.of(C2S.SPECTATE);
}

export function encodeOptions(opts: MessageBody<OptionsMessage>): Uint8Array<ArrayBuffer> {
  return encodeClientMessage({ op: C2S.OPTIONS, ...opts });
}

export function encodePing(clientTimeMs: number): Uint8Array<ArrayBuffer> {
  return encodeClientMessage({ op: C2S.PING, clientTimeMs });
}

// ---------------------------------------------------------------------------------------------
// Server → client
// ---------------------------------------------------------------------------------------------

export function writeWelcome(w: Writer, m: MessageBody<WelcomeMessage>): void {
  const flags =
    (m.customSkinsEnabled ? WELCOME_FLAG.CUSTOM_SKINS_ENABLED : 0) |
    (m.minimapPlayersAllowed ? WELCOME_FLAG.MINIMAP_PLAYERS_ALLOWED : 0) |
    (m.spectateAllowed ? WELCOME_FLAG.SPECTATE_ALLOWED : 0) |
    (m.customSkinsPublic ? WELCOME_FLAG.CUSTOM_SKINS_PUBLIC : 0) |
    (m.money !== undefined ? WELCOME_FLAG.MONEY_ROOM : 0) |
    (m.money?.practice === true ? WELCOME_FLAG.PRACTICE_ROOM : 0);
  w.u8(S2C.WELCOME)
    .u16(m.protocolVersion)
    .f32(m.tickRate)
    .str(m.serverBuild, STR_MAX_BYTES.serverBuild)
    .str(m.roomId, STR_MAX_BYTES.roomId)
    .u8(m.mode)
    .f32(m.minX)
    .f32(m.minY)
    .f32(m.maxX)
    .f32(m.maxY)
    .f32(m.posQ)
    .f32(m.radQ)
    .u8(m.maxCells)
    .u8(m.teamColors.length);
  for (const c of m.teamColors) w.rgb(c);
  w.vu(m.connectionId)
    .u8(flags)
    .str(m.partyCode, STR_MAX_BYTES.partyCode)
    .u16(m.leaderboardEveryTicks)
    .u16(m.minimapEveryTicks)
    .f32(m.virusMass)
    .f32(m.ejectPelletMass)
    .u8(m.virusFeedsToShoot);
  const money = m.money;
  if (money !== undefined) {
    w.f32(money.stakeUsd)
      .u16(fractionToWire(money.rake))
      .f32(money.solUsd)
      .f32(money.minLifeSec)
      .f32(money.cashOutHoldSec)
      .u8(money.labelCurrency === 'SOL' ? LABEL_CURRENCY.SOL : LABEL_CURRENCY.USD)
      .str(money.cashOutKey, STR_MAX_BYTES.cashOutKey);
  }
  // The one WELCOME change for the territory arena (protocol §5.3): a `u8 sim` trailer, written
  // only for arena rooms, so a cell room's WELCOME is byte-for-byte what it was.
  if (m.arena !== undefined) writeWelcomeArena(w, m.arena);
}

function writeWelcomeArena(w: Writer, a: WelcomeArenaInfo): void {
  w.u8(WELCOME_SIM.TERRITORY)
    .u16(a.cols)
    .u16(a.rows)
    .u8(a.shape === 'circle' ? ARENA_SHAPE_CODE.CIRCLE : ARENA_SHAPE_CODE.SQUARE)
    .u32(a.playableCells)
    .f32(a.speed)
    .f32(a.turnRateDeg)
    .f32(a.cellsDiagonal)
    .f32(a.zoomOutMax)
    .f32(a.shieldSec);
}

/** A percent (0–100) as `u16` hundredths, clamped. */
function pctWire(pct: number): number {
  const q = Number.isFinite(pct) ? Math.round(pct * 100) : 0;
  return q < 0 ? 0 : q > 10_000 ? 10_000 : q;
}

/** The arena trailer of DEATH and CASH_OUT_DONE (protocol §5.2). */
function writeArenaLife(w: Writer, a: ArenaLifeInfo): void {
  const flags =
    (a.atWall ? ARENA_LIFE_FLAG.AT_WALL : 0) | (a.forcedExit ? ARENA_LIFE_FLAG.FORCED_EXIT : 0);
  w.u8(a.cause)
    .u8(flags)
    .vu(a.killerPlayerId)
    .str(a.killerName, STR_MAX_BYTES.playerName)
    .u16(pctWire(a.landPct))
    .u16(pctWire(a.maxLandPct))
    .vu(a.kills)
    .vu(a.captures);
}

/** The LifeSummary fields shared by DEATH and CASH_OUT_DONE (protocol §5.2). */
function writeLifeSummary(w: Writer, m: LifeSummary, what: string): void {
  if (m.samples.length > MAX_MASS_SAMPLES) {
    throw new RangeError(`${what} has ${m.samples.length} samples (max ${MAX_MASS_SAMPLES})`);
  }
  w.u32(m.pelletsEaten)
    .u32(Math.floor(m.highestMass))
    .u32(m.timeAliveSec)
    .u32(m.boardTimeSec)
    .u32(m.ballsEaten)
    .u16(m.bestRank)
    .u16(m.sampleIntervalSec)
    .vu(m.samples.length);
  for (const s of m.samples) w.vu(Math.floor(s));
}

export function writeDeath(w: Writer, m: MessageBody<DeathMessage>): void {
  w.u8(S2C.DEATH);
  writeLifeSummary(w, m, 'DEATH');
  // Money trailer (money rooms only): present iff valueLost is set.
  if (m.valueLost !== undefined) w.vl(m.valueLost);
  // Arena trailer (arena rooms, always money or practice rooms): after valueLost.
  if (m.arena !== undefined) {
    if (m.valueLost === undefined) throw new RangeError('DEATH arena trailer needs valueLost');
    writeArenaLife(w, m.arena);
  }
}

export function writeCashOutState(w: Writer, m: MessageBody<CashOutStateMessage>): void {
  w.u8(S2C.CASH_OUT_STATE)
    .vu(m.playerId)
    .u8(m.active ? 1 : 0)
    .u16(fractionToWire(m.progress));
}

export function writeCashOutDone(w: Writer, m: MessageBody<CashOutDoneMessage>): void {
  w.u8(S2C.CASH_OUT_DONE).vl(m.value).f32(m.solUsd);
  writeLifeSummary(w, m.stats, 'CASH_OUT_DONE');
  if (m.arena !== undefined) writeArenaLife(w, m.arena);
}

export function writeMatchResult(w: Writer, m: MessageBody<MatchResultMessage>): void {
  w.u8(S2C.MATCH_RESULT).u8(m.mode);
  if (m.mode === 4) {
    if (m.standings.length > MAX_STANDINGS) {
      throw new RangeError(
        `MATCH_RESULT has ${m.standings.length} standings (max ${MAX_STANDINGS})`,
      );
    }
    w.u16(m.placement)
      .u16(m.totalPlayers)
      .u32(Math.floor(m.highestMass))
      .u32(m.ballsEaten)
      .u8(m.canSpectate ? 1 : 0)
      .u8(m.standings.length);
    for (const s of m.standings) {
      w.str(s.name, STR_MAX_BYTES.standingName).u8(s.isYou ? STANDING_FLAG.IS_YOU : 0);
    }
  } else {
    w.u8(m.winnerTeam).u8(m.shares.length);
    for (const share of m.shares) w.u16(fractionToWire(share));
    w.u32(Math.floor(m.highestMass)).u32(m.ballsEaten);
  }
}

/** Appends any server message to `w` (protocol §5.2). FRAME positions use `quant`. */
export function writeServerMessage(
  w: Writer,
  msg: ServerMessage,
  quant: Quant = DEFAULT_QUANT,
): void {
  switch (msg.op) {
    case S2C.WELCOME:
      writeWelcome(w, msg);
      return;
    case S2C.FRAME:
      writeFrame(w, msg, quant);
      return;
    case S2C.SPAWNED:
      w.u8(S2C.SPAWNED).vu(msg.playerId);
      return;
    case S2C.DEATH:
      writeDeath(w, msg);
      return;
    case S2C.PONG:
      w.u8(S2C.PONG).u32(Math.floor(msg.clientTimeMs) >>> 0);
      return;
    case S2C.NOTICE:
      w.u8(S2C.NOTICE).u16(msg.code).str(msg.arg, STR_MAX_BYTES.noticeArg);
      return;
    case S2C.PARTY:
      w.u8(S2C.PARTY).str(msg.code, STR_MAX_BYTES.partyCode).u8(msg.humanMembers);
      return;
    case S2C.MATCH_RESULT:
      writeMatchResult(w, msg);
      return;
    case S2C.CASH_OUT_STATE:
      writeCashOutState(w, msg);
      return;
    case S2C.CASH_OUT_DONE:
      writeCashOutDone(w, msg);
      return;
    case S2C.ERROR:
      w.u8(S2C.ERROR).u16(msg.code).str(msg.message, STR_MAX_BYTES.errorMessage);
      return;
  }
}

export function encodeServerMessage(
  msg: ServerMessage,
  quant: Quant = DEFAULT_QUANT,
): Uint8Array<ArrayBuffer> {
  const w = new Writer(msg.op === S2C.FRAME ? 1024 : 128);
  writeServerMessage(w, msg, quant);
  return w.finish();
}

export function encodeWelcome(m: MessageBody<WelcomeMessage>): Uint8Array<ArrayBuffer> {
  const w = new Writer(128);
  writeWelcome(w, m);
  return w.finish();
}

export function encodeFrame(
  f: MessageBody<FrameMessage>,
  quant: Quant = DEFAULT_QUANT,
): Uint8Array<ArrayBuffer> {
  const w = new Writer(1024);
  writeFrame(w, f, quant);
  return w.finish();
}

export function encodeSpawned(playerId: number): Uint8Array<ArrayBuffer> {
  return encodeServerMessage({ op: S2C.SPAWNED, playerId });
}

export function encodeDeath(m: MessageBody<DeathMessage>): Uint8Array<ArrayBuffer> {
  const w = new Writer(64 + m.samples.length * 2);
  writeDeath(w, m);
  return w.finish();
}

export function encodePong(clientTimeMs: number): Uint8Array<ArrayBuffer> {
  return encodeServerMessage({ op: S2C.PONG, clientTimeMs });
}

export function encodeNotice(code: number, arg = ''): Uint8Array<ArrayBuffer> {
  return encodeServerMessage({ op: S2C.NOTICE, code, arg });
}

export function encodeParty(code: string, humanMembers: number): Uint8Array<ArrayBuffer> {
  return encodeServerMessage({ op: S2C.PARTY, code, humanMembers });
}

export function encodeMatchResult(m: MessageBody<MatchResultMessage>): Uint8Array<ArrayBuffer> {
  const w = new Writer(128);
  writeMatchResult(w, m);
  return w.finish();
}

export function encodeCashOutState(m: MessageBody<CashOutStateMessage>): Uint8Array<ArrayBuffer> {
  const w = new Writer(16);
  writeCashOutState(w, m);
  return w.finish();
}

export function encodeCashOutDone(m: MessageBody<CashOutDoneMessage>): Uint8Array<ArrayBuffer> {
  const w = new Writer(64 + m.stats.samples.length * 2);
  writeCashOutDone(w, m);
  return w.finish();
}

export function encodeError(code: number, message = ''): Uint8Array<ArrayBuffer> {
  return encodeServerMessage({ op: S2C.ERROR, code, message });
}
